import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Dumbbell, Clock, Target, Flame, Heart, Brain, Loader2,
  Play, ChevronRight, Star, Filter, Sparkles, CheckCircle2,
  AlertCircle, Timer, User, Leaf, Youtube, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const CATEGORIES = [
  { id: 'all', name: 'All Poses' },
  { id: 'breathing', name: 'Breathing' },
  { id: 'meditation', name: 'Meditation' },
  { id: 'standing', name: 'Standing' },
  { id: 'back_strength', name: 'Back Strength' },
  { id: 'digestion', name: 'Digestion' },
  { id: 'relaxation', name: 'Relaxation' },
  { id: 'full_body', name: 'Full Body' }
];

const DIFFICULTIES = [
  { id: 'all', name: 'All Levels' },
  { id: 'beginner', name: 'Beginner' },
  { id: 'intermediate', name: 'Intermediate' },
  { id: 'advanced', name: 'Advanced' }
];

const YogaExercise = ({ user, setUser }) => {
  const { t } = useTranslation();
  const [poses, setPoses] = useState([]);
  const [routines, setRoutines] = useState([]);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('recommended');
  const [selectedPose, setSelectedPose] = useState(null);
  const [selectedRoutine, setSelectedRoutine] = useState(null);
  
  // Filters
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [difficultyFilter, setDifficultyFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [posesRes, routinesRes] = await Promise.all([
        axios.get(`${API}/yoga/poses`),
        axios.get(`${API}/yoga/routines`)
      ]);
      setPoses(posesRes.data.poses || []);
      setRoutines(routinesRes.data.routines || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGetRecommendations = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/yoga/recommend`);
      setRecommendations(response.data);
      toast.success('Personalized recommendations ready!');
    } catch (error) {
      toast.error('Failed to get recommendations');
    } finally {
      setGenerating(false);
    }
  };

  const filteredPoses = poses.filter(pose => {
    if (categoryFilter !== 'all' && pose.category !== categoryFilter) return false;
    if (difficultyFilter !== 'all' && pose.difficulty !== difficultyFilter) return false;
    return true;
  });

  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case 'beginner': return 'bg-green-100 text-green-700';
      case 'intermediate': return 'bg-yellow-100 text-yellow-700';
      case 'advanced': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-green-50">
        <Navigation user={user} setUser={setUser} />
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-green-50">
      <Navigation user={user} setUser={setUser} />
      
      <div className="container mx-auto px-4 py-6 pt-24">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-teal-600 to-green-600">
              <Leaf className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800">Yoga & Exercise</h1>
              <p className="text-gray-600">AI-powered wellness recommendations</p>
            </div>
          </div>
          
          <Button 
            onClick={handleGetRecommendations}
            disabled={generating}
            className="bg-gradient-to-r from-teal-600 to-green-600"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
            Get AI Recommendations
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList>
            <TabsTrigger value="recommended">For You</TabsTrigger>
            <TabsTrigger value="routines">Routines</TabsTrigger>
            <TabsTrigger value="library">Pose Library</TabsTrigger>
          </TabsList>

          {/* Recommended Tab */}
          <TabsContent value="recommended" className="space-y-6">
            {!recommendations ? (
              <Card className="p-8 text-center">
                <Sparkles className="w-16 h-16 mx-auto text-teal-300 mb-4" />
                <h3 className="text-xl font-semibold mb-2">Get Personalized Recommendations</h3>
                <p className="text-gray-500 mb-4">Our AI analyzes your health profile to suggest the best yoga poses and exercises for you</p>
                <Button onClick={handleGetRecommendations} disabled={generating}>
                  {generating ? 'Generating...' : 'Generate Recommendations'}
                </Button>
              </Card>
            ) : (
              <>
                {/* Health Assessment */}
                <Card className="bg-gradient-to-r from-teal-600 to-green-600 text-white">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <Brain className="w-8 h-8" />
                      <div>
                        <h3 className="text-lg font-semibold mb-2">Your Health Assessment</h3>
                        <p className="text-teal-100">{recommendations.health_assessment}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Custom Routine */}
                {recommendations.custom_routine && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Target className="w-5 h-5 text-teal-600" />
                        Your Personalized Routine
                      </CardTitle>
                      <CardDescription>
                        {recommendations.custom_routine.name} • {recommendations.custom_routine.duration} minutes
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {recommendations.custom_routine.poses?.map((pose, idx) => {
                          const poseDetails = poses.find(p => p.id === pose.pose_id);
                          return (
                            <div key={`item-${idx}`} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                              <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center text-teal-600 font-bold">
                                {idx + 1}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium">{poseDetails?.name || pose.pose_id}</p>
                                <p className="text-sm text-gray-500">{pose.notes}</p>
                              </div>
                              <Badge variant="outline">{pose.duration} min</Badge>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Recommended Poses */}
                <h3 className="text-lg font-semibold">Recommended Poses for You</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {recommendations.recommended_poses?.map((pose) => (
                    <Card 
                      key={pose.id} 
                      className="cursor-pointer hover:shadow-lg transition-all"
                      onClick={() => setSelectedPose(pose)}
                    >
                      <CardContent className="p-4">
                        <img 
                          src={pose.image_url} 
                          alt={pose.name}
                          className="w-full h-40 object-cover rounded-lg mb-3"
                          onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400'}
                        />
                        <h4 className="font-semibold">{pose.name}</h4>
                        <p className="text-sm text-gray-500">{pose.sanskrit}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge className={getDifficultyColor(pose.difficulty)}>{pose.difficulty}</Badge>
                          <Badge variant="outline">{pose.duration_minutes} min</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Tips & Precautions */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-green-600 flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5" />
                        Exercise Tips
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {recommendations.exercise_tips?.map((tip, idx) => (
                          <li key={`item-${idx}`} className="flex items-start gap-2 text-gray-700">
                            <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-amber-600 flex items-center gap-2">
                        <AlertCircle className="w-5 h-5" />
                        Precautions
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {recommendations.precautions?.map((precaution, idx) => (
                          <li key={`item-${idx}`} className="flex items-start gap-2 text-gray-700">
                            <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                            {precaution}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                </div>

                {/* Weekly Plan */}
                <Card className="bg-teal-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Timer className="w-6 h-6 text-teal-600" />
                      <div>
                        <p className="font-medium text-teal-800">Weekly Plan</p>
                        <p className="text-teal-600">{recommendations.weekly_plan}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Routines Tab */}
          <TabsContent value="routines" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {routines.map((routine) => (
                <Card 
                  key={routine.id} 
                  className="cursor-pointer hover:shadow-lg transition-all"
                  onClick={() => setSelectedRoutine(routine)}
                >
                  <CardHeader>
                    <CardTitle className="text-lg">{routine.name}</CardTitle>
                    <CardDescription>{routine.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span className="text-sm">{routine.duration} min</span>
                      </div>
                      <Badge className={getDifficultyColor(routine.difficulty)}>
                        {routine.difficulty}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">{routine.poses?.length} poses</span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Library Tab */}
          <TabsContent value="library" className="space-y-6">
            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={difficultyFilter} onValueChange={setDifficultyFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Difficulty" />
                </SelectTrigger>
                <SelectContent>
                  {DIFFICULTIES.map((diff) => (
                    <SelectItem key={diff.id} value={diff.id}>{diff.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Badge variant="outline" className="py-2">
                {filteredPoses.length} poses found
              </Badge>
            </div>

            {/* Poses Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredPoses.map((pose) => (
                <Card 
                  key={pose.id} 
                  className="cursor-pointer hover:shadow-lg transition-all group"
                  onClick={() => setSelectedPose(pose)}
                >
                  <CardContent className="p-4">
                    <div className="relative">
                      <img 
                        src={pose.image_url} 
                        alt={pose.name}
                        className="w-full h-32 object-cover rounded-lg mb-3"
                        onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400'}
                      />
                      {pose.video_id && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                          <div className="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center">
                            <Play className="w-6 h-6 text-white fill-white" />
                          </div>
                        </div>
                      )}
                      {pose.video_id && (
                        <div className="absolute top-2 right-2 bg-red-600 rounded-full p-1">
                          <Youtube className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    <h4 className="font-medium text-sm">{pose.name}</h4>
                    <p className="text-xs text-gray-500 mb-2">{pose.sanskrit}</p>
                    <div className="flex items-center gap-2">
                      <Badge className={`${getDifficultyColor(pose.difficulty)} text-xs`}>
                        {pose.difficulty}
                      </Badge>
                      <span className="text-xs text-gray-500">{pose.duration_minutes} min</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Pose Detail Dialog */}
      <Dialog open={!!selectedPose} onOpenChange={() => setSelectedPose(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedPose && (
            <>
              <DialogHeader>
                <DialogTitle className="text-xl">{selectedPose.name}</DialogTitle>
                <DialogDescription>{selectedPose.sanskrit}</DialogDescription>
              </DialogHeader>
              
              <img 
                src={selectedPose.image_url} 
                alt={selectedPose.name}
                className="w-full h-48 object-cover rounded-lg"
                onError={(e) => e.target.src = 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400'}
              />
              
              <div className="flex items-center gap-3 my-4">
                <Badge className={getDifficultyColor(selectedPose.difficulty)}>
                  {selectedPose.difficulty}
                </Badge>
                <Badge variant="outline">
                  <Clock className="w-3 h-3 mr-1" />
                  {selectedPose.duration_minutes} minutes
                </Badge>
                <Badge variant="outline">{selectedPose.category}</Badge>
              </div>
              
              {/* Benefits */}
              <div className="mb-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Heart className="w-4 h-4 text-green-500" />
                  Benefits
                </h4>
                <ul className="grid grid-cols-2 gap-2">
                  {selectedPose.benefits?.map((benefit, idx) => (
                    <li key={`item-${idx}`} className="text-sm text-gray-600 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-green-500" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Steps */}
              <div className="mb-4">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Target className="w-4 h-4 text-blue-500" />
                  How to Practice
                </h4>
                <ol className="space-y-2">
                  {selectedPose.steps?.map((step, idx) => (
                    <li key={`item-${idx}`} className="flex gap-3">
                      <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {idx + 1}
                      </span>
                      <span className="text-sm text-gray-600">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
              
              {/* Suitable For */}
              <div className="mb-4">
                <h4 className="font-semibold mb-2">Suitable For</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedPose.suitable_for?.map((condition, idx) => (
                    <Badge key={`item-${idx}`} variant="outline" className="bg-teal-50">
                      {condition.replace('_', ' ')}
                    </Badge>
                  ))}
                </div>
              </div>
              
              {/* Video Tutorial */}
              {selectedPose.video_id && (
                <div className="mb-4">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Youtube className="w-4 h-4 text-red-500" />
                    Video Tutorial
                  </h4>
                  <div className="relative aspect-video rounded-lg overflow-hidden bg-gray-100">
                    <iframe
                      width="100%"
                      height="100%"
                      src={`https://www.youtube.com/embed/${selectedPose.video_id}`}
                      title={selectedPose.video_title || selectedPose.name}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="absolute inset-0"
                    />
                  </div>
                  <a 
                    href={selectedPose.video_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-red-600 hover:underline flex items-center gap-1 mt-2"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {selectedPose.video_title || 'Watch on YouTube'}
                  </a>
                </div>
              )}

              {/* Contraindications */}
              {selectedPose.contraindications?.length > 0 && (
                <div className="p-3 bg-amber-50 rounded-lg">
                  <h4 className="font-semibold mb-2 text-amber-800 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Avoid If
                  </h4>
                  <ul className="text-sm text-amber-700">
                    {selectedPose.contraindications.map((item, idx) => (
                      <li key={`item-${idx}`}>• {item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Routine Detail Dialog */}
      <Dialog open={!!selectedRoutine} onOpenChange={() => setSelectedRoutine(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedRoutine && (
            <>
              <DialogHeader>
                <DialogTitle>{selectedRoutine.name}</DialogTitle>
                <DialogDescription>{selectedRoutine.description}</DialogDescription>
              </DialogHeader>
              
              <div className="flex items-center gap-4 my-4">
                <Badge className={getDifficultyColor(selectedRoutine.difficulty)}>
                  {selectedRoutine.difficulty}
                </Badge>
                <Badge variant="outline">
                  <Clock className="w-3 h-3 mr-1" />
                  {selectedRoutine.duration} minutes
                </Badge>
              </div>
              
              <div className="space-y-3">
                {selectedRoutine.poses?.map((pose, idx) => {
                  const poseDetails = poses.find(p => p.id === pose.pose_id);
                  return (
                    <div 
                      key={`item-${idx}`} 
                      className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100"
                      onClick={() => {
                        setSelectedRoutine(null);
                        if (poseDetails) setSelectedPose(poseDetails);
                      }}
                    >
                      <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center text-teal-600 font-bold">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{poseDetails?.name || pose.pose_id}</p>
                        <p className="text-sm text-gray-500">{poseDetails?.sanskrit}</p>
                      </div>
                      <Badge variant="outline">{pose.duration} min</Badge>
                    </div>
                  );
                })}
              </div>
              
              <Button className="w-full mt-4 bg-gradient-to-r from-teal-600 to-green-600">
                <Play className="w-4 h-4 mr-2" />
                Start Routine
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default YogaExercise;